import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineDestroyApiOptions {
  disciplineId: string | number
  onStart?: () => void
  onSuccess?: () => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `DELETE /admin-api/disciplines/{discipline}`. A 422 names why nothing was deleted (finished lessons,
 *  an active subscription), so its message is shown verbatim. */
export const useDisciplineDestroyApi = async (options: DisciplineDestroyApiOptions): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.disciplines.api.destroy', { discipline_id: options.disciplineId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return false
    }

    await $axios.delete<ApiResponse>(url)

    toast.success({ title: 'Successfully deleted!', description: 'Discipline was successfully deleted!' })
    options.onSuccess?.()

    return true
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error({ title: 'Discipline cannot be deleted!', description: msg })
    options.onError?.(msg)

    return false
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
